import { appHistory } from "../appHistory";
import { ApiError, IApiBody, IApiOptions, IApiResponse } from "../models/api";

const DEFAULT_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json"
};

export class Api {
  // @ts-ignore
  baseUrl = process.env.REACT_APP_API_URL;

  find<T>(endpoint: string, options?: IApiOptions): Promise<T> {
    return this._request<T, any>(endpoint, "GET", undefined, options);
  }

  post<T, U extends IApiBody>(
    endpoint: string,
    body: U,
    options?: IApiOptions
  ): Promise<T> {
    return this._request<T, U>(endpoint, "POST", body, options);
  }

  create<T, U extends IApiBody>(
    endpoint: string,
    body: U,
    options?: IApiOptions
  ): Promise<T> {
    return this.post<T, U>(endpoint, body, options);
  }

  put<T, U extends IApiBody>(
    endpoint: string,
    body?: U,
    options?: IApiOptions
  ): Promise<T> {
    return this._request<T, U>(endpoint, "PUT", body, options);
  }

  update<T, U extends IApiBody>(
    endpoint: string,
    body?: U,
    options?: IApiOptions
  ): Promise<T> {
    return this.put<T, U>(endpoint, body, options);
  }

  delete<T>(endpoint: string, options?: IApiOptions): Promise<T> {
    let defaultOptions: IApiOptions = {
      dontParseResponse: true
    };

    return this._request<T, any>(
      endpoint,
      "DELETE",
      undefined,
      Object.assign({}, defaultOptions, options)
    );
  }

  findFile<T>(endpoint: string, options?: IApiOptions): Promise<T> {
    if (!options) options = { headers: {} };
    if (!options.headers) options.headers = {};
    options.headers = {
      Accept: "application/json",
      "Content-Type": "application/json"
    };
    options.dontParseResponse = true;
    /* options.headers['responseType'] = 'blob'
        options.headers['Accept'] = 'application/pdf'
        options.headers['Content-Type'] = 'application/json' */
    return this._request<T, any>(endpoint, "GET", undefined, options);
  }

  downloadFile<T>(endpoint: string, options?: IApiOptions): Promise<T> {
    if (!options) options = { };
    options.headers = {
      Accept: "application/json",
      "Content-Type": "application/json"
    };
    options.dontParseResponse = true;
    return this._request<T, any>(endpoint, "GET", undefined, options);
  }

  async _request<T, U extends IApiBody>(
    endpoint: string,
    method = "GET",
    body?: U,
    options?: IApiOptions
  ): Promise<T> {
    let url = this._constructApiUrlFromEndpoint(endpoint);
    let accessToken =
      typeof window !== "undefined" && (options || {}).dontIncludeJwt !== true
        ? window.localStorage.getItem("access_token")
        : null;

    const requestOptions: {
      method: string;
      headers: { [id: string]: string };
      body?: string;
    } = {
      method,
      headers: (options && options.headers) || DEFAULT_HEADERS
    };

    if (accessToken) {
      requestOptions.headers["Authorization"] = `Bearer ${accessToken}`;
    } else {
      // clear any cached token to resolve non-authenticated errors
      delete requestOptions.headers['Authorization']
    }

    if (body) {
      requestOptions.body = JSON.stringify(body);
    }

    let fetchResponse: Response;
    try {
      fetchResponse = await fetch(url, requestOptions);
    } catch (error) {
      let message =
        typeof error === "string" ? error
        : error instanceof Error ? error.message : "Error"
      throw new ApiError(message, 0);
    }
    if (!options || options.dontCatchBannedResponses !== true) {
      fetchResponse = this._catchForbiddenRequests(fetchResponse);
    }

    const response = await this._parseJSON<T>(fetchResponse, options?.dontParseResponse);
    if (!response.ok) {
      throw new ApiError(
        "API Error occurred",
        response.status,
        response.payload
      );
    }

    return response.payload;
  }

  _catchForbiddenRequests(response: Response): Response {
    if (response.status === 401 || response.status === 403) {
      // Log.info(
      //   "User is not authenticated or authorized, redirect to login page"
      // );
      appHistory.push("/login");
      // userStore.logout();
      // notificationsStore.showNotification(
      //   "Sorry, you have to login to view this page"
      // );
    }

    return response;
  }

  _constructApiUrlFromEndpoint(endpoint: string): string {
    return `${this.baseUrl}/${endpoint}`;
  }

  async _parseJSON<T>(response: Response, dontParseResponse = false): Promise<IApiResponse<T>> {
    const payload = await response.text();
    let parsedPayload;
    if (payload && dontParseResponse === true) parsedPayload = payload;
    if (payload && dontParseResponse === false) parsedPayload = JSON.parse(payload);

    return {
      status: response.status,
      ok: response.ok,
      payload: payload ? parsedPayload : {}
    };
  }
}

export const api = new Api();
